import { useEffect, useState } from "react";
import { Facebook, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

// Extend Window interface for Facebook SDK
declare global {
  interface Window {
    FB?: {
      XFBML: {
        parse: () => void;
      };
    };
  }
}

const FacebookFeed = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFacebookSDK = () => {
      if (document.getElementById("facebook-jssdk")) {
        if (window.FB) {
          window.FB.XFBML.parse();
          setIsLoading(false);
        }
        return;
      }

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v24.0&appId=818384474544954";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);

      script.onload = () => {
        setTimeout(() => {
          if (window.FB) {
            window.FB.XFBML.parse();
          }
          setIsLoading(false);
        }, 1000);
      };
    };

    loadFacebookSDK();

    if (window.FB) {
      window.FB.XFBML.parse();
      setIsLoading(false);
    }
  }, []);

  return (
    <div className="py-12">
      <div className="flex flex-col lg:flex-row gap-8 max-w-4xl mx-auto justify-center">
        {/* Sidebar */}
        <aside className="lg:w-72 flex-shrink-0 order-2 lg:order-1" aria-label="Social media links and volunteer call-to-action">
          <div className="lg:sticky lg:top-24 flex flex-col md:flex-row lg:flex-col gap-6">
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm md:flex-1 lg:flex-none">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#1877f2] rounded-full flex items-center justify-center">
                  <Facebook className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold text-foreground">
                    Stay Connected
                  </h2>
                  <p className="font-body text-sm text-muted-foreground">
                    Follow Matt on Facebook
                  </p>
                </div>
              </div>
              <p className="font-body text-muted-foreground mb-4">
                Get real-time updates, photos from campaign events, and connect with other supporters.
              </p>
              <Button variant="navy" className="w-full" asChild>
                <a
                  href="https://www.facebook.com/profile.php?id=100077991582201"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-4 w-4 mr-2" />
                  Follow on Facebook
                  <ExternalLink className="h-3 w-3 ml-2" />
                </a>
              </Button>
            </div>

            <div className="bg-primary/5 rounded-xl border border-primary/20 p-6 md:flex-1 lg:flex-none">
              <h3 className="font-display text-lg font-bold text-foreground mb-3">
                Get Involved
              </h3>
              <p className="font-body text-sm text-muted-foreground mb-4">
                Want to help Matt win in November? Join our team of dedicated volunteers.
              </p>
              <Button variant="cta" size="sm" className="w-full" asChild>
                <a href="/volunteer">Volunteer Now</a>
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Feed */}
        <div className="w-full md:max-w-[510px] lg:max-w-[510px] order-1 lg:order-2">
          <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-[#1877f2] to-[#0d6efd] px-6 py-4">
              <div className="flex items-center gap-3">
                <Facebook className="h-6 w-6 text-white" />
                <span className="font-display font-bold text-white text-lg">
                  Latest Posts
                </span>
              </div>
            </div>
            
            <div className="p-6 min-h-[600px] relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-card">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                    <p className="font-body text-muted-foreground">Loading Facebook feed...</p>
                  </div>
                </div>
              )}
              
              <div
                className="fb-page"
                data-href="https://www.facebook.com/profile.php?id=100077991582201#"
                data-tabs="timeline"
                data-width="500"
                data-height=""
                data-small-header="true"
                data-adapt-container-width="true"
                data-hide-cover="true"
                data-show-facepile="false"
              >
                <blockquote
                  cite="https://www.facebook.com/profile.php?id=100077991582201#"
                  className="fb-xfbml-parse-ignore"
                >
                  <a href="https://www.facebook.com/profile.php?id=100077991582201#" className="text-primary hover:underline">
                    Matt Bucher for 33rd District Representative Delaware
                  </a>
                </blockquote>
              </div>
            </div>
          </div>

          {/* Direct link fallback */}
          <div className="mt-6 text-center">
            <p className="font-body text-sm text-muted-foreground mb-2">
              Feed not loading? Visit the page directly:
            </p>
            <a
              href="https://www.facebook.com/profile.php?id=100077991582201"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <Facebook className="h-4 w-4" />
              Matt Bucher for State Representative
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacebookFeed;
